import lodash from 'lodash'
import type { Profile } from '../../../app-core/lib/user/Profile'
import type { UserProfile } from '../../../shared/domain/users/types.no-deps'
import { setAllDeleted, setAllNotDeleted } from '../../../shared/lib/functional/deletable.no-deps'
import { IDB_syncParamsUserProfiles, IDB_userProfiles } from '../IDB/IDB.dexie'
import { Serv_syncUserProfiles } from '../Serv/Serv.users'
import { Sync_standardSync } from '../Sync/Sync.standardSync'
import { ensureProfileImagesCached } from './UserProfileImageCache'

export async function User_profilesSync() {
    await Sync_standardSync({
        params: IDB_syncParamsUserProfiles(),
        sync: Serv_syncUserProfiles,
        write,
        clear: () => IDB_userProfiles().clear(),
    })
}

export async function User_profilesFetch(userIds: number[]) {
    const userProfiles: { [userId: number]: Profile } = {}

    await IDB_userProfiles()
        .where('userId')
        .anyOf(userIds)
        .each((userProfile) => (userProfiles[userProfile.userId] = userProfile))

    return userProfiles
}

// function to fetch all user profiles from indexed db including the deleted ones
export async function User_profilesFetchAll() {
    const userProfiles = await IDB_userProfiles().toArray()
    return lodash.sortBy(userProfiles, 'name')
}

// function to fetch all user profiles from indexed db and filtering the deleted ones
export async function User_profilesFetchExisting() {
    const userProfiles = await IDB_userProfiles()
        .filter((userProfile) => !userProfile.isDeleted)
        .toArray()

    return lodash.sortBy(userProfiles, 'name')
}

export async function User_profilesFetchByAccess(access: Array<'confidentialReports'>) {
    const userProfiles = await IDB_userProfiles()
        .filter((userProfile) => !userProfile.isDeleted)
        .toArray()

    const sorted = lodash.sortBy(userProfiles, 'name')

    return sorted.filter((userProfile) => {
        return access.every((priv) => userProfile.access?.[priv])
    })
}

interface ProfilesSyncResponse {
    syncTime: Date
    teams: number[]
    profiles: UserProfile[]
    deletedProfiles: number[]
}

async function write(response: ProfilesSyncResponse) {
    const { profiles, deletedProfiles } = response

    if (profiles.length > 0) {
        await IDB_userProfiles().bulkPut(setAllNotDeleted(profiles))
    }

    if (deletedProfiles.length > 0) {
        await IDB_userProfiles()
            .where('userId')
            .anyOf(deletedProfiles)
            .toArray()
            .then(setAllDeleted)
            .then((userProfiles) => IDB_userProfiles().bulkPut(userProfiles))
    }

    // no need to await this; let it run in the background
    ensureProfileImagesCached()
}
